/**
 * SearchBar utility functions
 * 
 * Contains helper functions for SearchBar component:
 * - Text highlighting for search matches
 * - Suggestion generation logic
 * - Key normalization utilities
 */

import React from 'react';
import { SearchResult, AutocompleteSuggestion } from '../types/search';

/**
 * Highlight matching text in suggestions
 * Creates JSX elements with highlighted search terms
 */
export const highlightMatch = (text: string, query: string): JSX.Element => {
  if (!query.trim()) {
    return React.createElement('span', {}, text);
  }

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);

  return React.createElement(
    'span',
    {},
    parts.map((part, index) =>
      regex.test(part) ? (
        React.createElement(
          'mark',
          { key: index, className: 'bg-yellow-200 font-medium' },
          part
        )
      ) : (
        React.createElement('span', { key: index }, part)
      )
    )
  );
};

/**
 * Generate autocomplete suggestions from recent searches and results
 * Combines recent searches and search results into prioritized suggestions
 */
export const generateSuggestions = (
  query: string,
  results: SearchResult[],
  recentSearches: string[],
  maxSuggestions: number,
  showRecentSearches: boolean
): AutocompleteSuggestion[] => {
  const suggestions: AutocompleteSuggestion[] = [];
  const lowerQuery = query.toLowerCase();

  // Add recent searches that match query
  if (showRecentSearches && query.length > 0) {
    const matchingRecent = recentSearches
      .filter(search => search.toLowerCase().includes(lowerQuery))
      .slice(0, 3)
      .map((search, index) => ({
        id: `recent-${index}`,
        text: search,
        type: 'recent' as const,
      }));

    suggestions.push(...matchingRecent);
  }

  // Add search results as suggestions
  const resultSuggestions = results
    .slice(0, maxSuggestions - suggestions.length)
    .map((result) => ({
      id: `result-${result.id}`,
      text: result.title,
      type: 'result' as const,
      category: result.category,
    }));

  suggestions.push(...resultSuggestions);

  return suggestions.slice(0, maxSuggestions);
};

/**
 * Generate unique component ID for accessibility
 * Creates a unique identifier for ARIA attributes
 */
export const generateComponentId = (): string => {
  return `search-bar-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Safe state updater factory
 * Creates a state updater that only updates if component is still mounted
 */
export const createSafeStateUpdater = <T>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  mountedRef: React.MutableRefObject<boolean>
) => {
  return (updater: (prev: T) => T) => {
    if (mountedRef.current) {
      setState(updater);
    }
  };
};

/**
 * Scroll element into view smoothly
 * Helper for keyboard navigation
 */
export const scrollIntoViewSmooth = (element: HTMLElement): void => {
  element.scrollIntoView({
    block: 'nearest',
    behavior: 'smooth',
  });
};